import React, { useState, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import { Link } from 'gatsby'
import * as styles from './styles'

const propTypes = {
  cases: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    metric: PropTypes.string.isRequired,
    metricLabel: PropTypes.string.isRequired,
    query: PropTypes.string.isRequired,
    demoUrl: PropTypes.string.isRequired,
    demoHash: PropTypes.string.isRequired,
    imageUrl: PropTypes.string,
  })).isRequired,
}

const UseCaseCarousel = ({ cases }) => {
  const [activeIndex, setActiveIndex] = useState(0)
  const [isPaused, setIsPaused] = useState(false)
  const [showCopiedTooltip, setShowCopiedTooltip] = useState(false)
  const intervalRef = useRef(null)

  // Auto-advance carousel
  useEffect(() => {
    if (!isPaused) {
      intervalRef.current = setInterval(() => {
        setActiveIndex((prevIndex) => (prevIndex + 1) % cases.length)
      }, 6000)
    }
    return () => clearInterval(intervalRef.current)
  }, [isPaused, cases.length])

  const handleCopyQuery = async (query) => {
    try {
      await navigator.clipboard.writeText(query)
      setShowCopiedTooltip(true)
      setTimeout(() => setShowCopiedTooltip(false), 2000)
    } catch (err) {
      console.error('Failed to copy query:', err)
    }
  }

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowLeft') {
      setActiveIndex((prevIndex) => (prevIndex - 1 + cases.length) % cases.length)
      setIsPaused(true)
    } else if (e.key === 'ArrowRight') {
      setActiveIndex((prevIndex) => (prevIndex + 1) % cases.length)
      setIsPaused(true)
    }
  }

  const handleDotClick = (index) => {
    setActiveIndex(index)
    setIsPaused(true)
  }

  const handleSwipe = (direction) => {
    if (direction === 'left') {
      setActiveIndex((prevIndex) => (prevIndex + 1) % cases.length)
    } else if (direction === 'right') {
      setActiveIndex((prevIndex) => (prevIndex - 1 + cases.length) % cases.length)
    }
    setIsPaused(true)
  }

  // Touch handling for mobile swipe
  const touchStartX = useRef(0)
  const touchEndX = useRef(0)

  const handleTouchStart = (e) => {
    touchStartX.current = e.changedTouches[0].screenX
  }

  const handleTouchEnd = (e) => {
    touchEndX.current = e.changedTouches[0].screenX
    const diff = touchStartX.current - touchEndX.current
    if (Math.abs(diff) > 50) {
      handleSwipe(diff > 0 ? 'left' : 'right')
    }
  }

  const activeCase = cases[activeIndex]

  return (
    <styles.CarouselContainer
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      onFocus={() => setIsPaused(true)}
      onBlur={() => setIsPaused(false)}
      onKeyDown={handleKeyDown}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      tabIndex={0}
      role="region"
      aria-label="Use case carousel"
      aria-roledescription="carousel"
    >
      <styles.SectionHeader>
        <styles.SectionTitle>See Aito in Action</styles.SectionTitle>
        <styles.SectionSubtitle>
          Real use cases with actual query syntax and production results
        </styles.SectionSubtitle>
      </styles.SectionHeader>
      <styles.CarouselContent>
        <styles.CarouselCard
          role="group"
          aria-roledescription="slide"
          aria-label={`${activeIndex + 1} of ${cases.length}`}
        >
          <styles.CardLeft>
            <styles.CardHeader>
              <styles.CardTitle>{activeCase.title}</styles.CardTitle>
              <styles.MetricBadge>
                <styles.MetricValue>{activeCase.metric}</styles.MetricValue>
                <styles.MetricLabel>{activeCase.metricLabel}</styles.MetricLabel>
              </styles.MetricBadge>
            </styles.CardHeader>

            <styles.CodeSection>
              <styles.CodeTeaser>{activeCase.query}</styles.CodeTeaser>
              <styles.CopyButton
                onClick={() => handleCopyQuery(activeCase.query)}
                onKeyPress={(e) => e.key === 'Enter' && handleCopyQuery(activeCase.query)}
                tabIndex={0}
                role="button"
                aria-label="Copy query"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                  <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                </svg>
                {showCopiedTooltip && <styles.CopiedTooltip>Copied!</styles.CopiedTooltip>}
              </styles.CopyButton>
            </styles.CodeSection>

            <styles.CardFooter>
              <styles.DemoLink 
                as={Link}
                to={`${activeCase.demoUrl}${activeCase.demoHash}`}
              >
                Try live demo →
              </styles.DemoLink>
            </styles.CardFooter>
          </styles.CardLeft>

          <styles.CardRight>
            {activeCase.imageUrl && activeCase.imageUrl.childImageSharp && (
              <styles.CardImage>
                <img 
                  src={activeCase.imageUrl.childImageSharp.fluid.src} 
                  srcSet={activeCase.imageUrl.childImageSharp.fluid.srcSet}
                  sizes={activeCase.imageUrl.childImageSharp.fluid.sizes}
                  alt={`${activeCase.title} demonstration`}
                  loading={activeIndex === 0 ? "eager" : "lazy"}
                />
              </styles.CardImage>
            )}
          </styles.CardRight>
        </styles.CarouselCard>
      </styles.CarouselContent>

      <styles.CarouselControls>
        <styles.DotsContainer role="tablist">
          {cases.map((_, index) => (
            <styles.Dot
              key={index}
              active={index === activeIndex}
              onClick={() => handleDotClick(index)}
              onKeyPress={(e) => e.key === 'Enter' && handleDotClick(index)}
              tabIndex={0}
              role="tab"
              aria-selected={index === activeIndex}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </styles.DotsContainer>
      </styles.CarouselControls>
    </styles.CarouselContainer>
  )
}

UseCaseCarousel.propTypes = propTypes

export default UseCaseCarousel